import React, { useMemo } from 'react';
import {
    Paper,
    Box,
    Typography,
    useTheme,
} from '@mui/material';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { MEASURE_CONFIG, getMeasureLabel, getMeasureUnit } from '../../utils/measurementConfig';
import { getZoneColor } from '../../utils/monthlyDataUtils';

function CustomTooltip({ active, payload, label, seriesType, measureType, t, selectedYear }) {
    const theme = useTheme();

    if (!active || !payload || payload.length === 0) {
        return null;
    }

    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            sx={{
                bgcolor: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1.5,
                boxShadow: 4,
                minWidth: 160,
                backdropFilter: 'blur(4px)',
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {label} {selectedYear ? `(${selectedYear})` : ''}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {payload.map((entry, index) => {
                    if (entry.value === undefined || entry.value === null) return null;

                    let displayLabel = entry.name;
                    let unit = '';
                    let color = entry.color || entry.stroke || '#888';

                    if (seriesType === 'metric') {
                        displayLabel = getMeasureLabel(entry.dataKey, t);
                        unit = getMeasureUnit(entry.dataKey);
                        color = MEASURE_CONFIG[entry.dataKey]?.color || color;
                    } else if (measureType && measureType !== 'ALL') {
                        unit = getMeasureUnit(measureType);
                    }

                    return (
                        <Box
                            key={`item-${index}`}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                fontSize: '0.8125rem',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: color,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 140 }}>
                                    {displayLabel}
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {entry.value} {unit}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function MonthlyTrendChart({
    trendData,
    chartType = 'line',
    activeMeasure = 'ALL',
    selectedYear,
    selectedZone = 'all',
}) {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const { chartData, seriesKeys, seriesType, measureType } = trendData || {
        chartData: [],
        seriesKeys: [],
        seriesType: 'metric',
        measureType: 'ALL',
    };

    // Calculate domain for single metric or multiple metrics
    const axisConfigs = useMemo(() => {
        if (seriesType === 'metric') {
            // Need separate axes for each metric if multi-metric
            const configs = {};
            seriesKeys.forEach((key) => {
                const values = chartData
                    .map((d) => d[key])
                    .filter((v) => v !== undefined && v !== null && !isNaN(v));

                if (values.length > 0) {
                    const min = Math.min(...values);
                    const max = Math.max(...values);
                    const buffer = (max - min) * 0.15 || 2;
                    configs[key] = {
                        min: Math.max(0, Math.floor(min - buffer)),
                        max: Math.ceil(max + buffer),
                        hasData: true,
                    };
                } else {
                    configs[key] = { min: 0, max: 100, hasData: false };
                }
            });
            return configs;
        } else {
            // Single measure across multiple zones
            const allValues = chartData.flatMap((d) =>
                seriesKeys.map((k) => d[k]).filter((v) => v !== undefined && v !== null && !isNaN(v))
            );

            if (allValues.length > 0) {
                const min = Math.min(...allValues);
                const max = Math.max(...allValues);
                const buffer = (max - min) * 0.15 || 2;
                return {
                    single: {
                        min: Math.floor(min - buffer),
                        max: Math.ceil(max + buffer),
                        hasData: true,
                    },
                };
            }
            return { single: { min: 0, max: 100, hasData: false } };
        }
    }, [chartData, seriesKeys, seriesType]);

    const titleText = useMemo(() => {
        if (activeMeasure !== 'ALL') {
            const mLabel = getMeasureLabel(activeMeasure, t);
            if (selectedZone !== 'all') {
                return `${mLabel} - ${selectedZone} (${selectedYear})`;
            }
            return `${mLabel} ${t('common.trendOverTime', '12-Month Trend')} (${selectedYear})`;
        }
        if (selectedZone !== 'all') {
            return `${selectedZone} - ${t('common.trendOverTime', '12-Month Trend')} (${selectedYear})`;
        }
        return `${t('common.allMeasures', 'All Measures')} - ${t('common.trendOverTime', '12-Month Trend')} (${selectedYear})`;
    }, [activeMeasure, selectedZone, selectedYear, t]);

    const ChartComponent = chartType === 'area' ? AreaChart : LineChart;

    return (
        <Paper
            elevation={2}
            sx={{
                p: { xs: 2, sm: 3 },
                mb: 3,
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
            }}
        >
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {titleText}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {t('common.year', 'Year')}: {selectedYear}
                </Typography>
            </Box>

            <Box sx={{ width: '100%', height: 380 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ChartComponent
                        data={chartData}
                        margin={{ top: 10, right: 30, left: 10, bottom: 5 }}
                    >
                        {/* Gradients for Area Chart */}
                        <defs>
                            {seriesType === 'metric' ? (
                                seriesKeys.map((key) => {
                                    const color = MEASURE_CONFIG[key]?.color || '#8884d8';
                                    return (
                                        <linearGradient key={`grad-${key}`} id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={color} stopOpacity={0.4} />
                                            <stop offset="95%" stopColor={color} stopOpacity={0.02} />
                                        </linearGradient>
                                    );
                                })
                            ) : (
                                seriesKeys.map((key, idx) => {
                                    const color = getZoneColor(idx);
                                    return (
                                        <linearGradient key={`grad-${key}`} id={`grad-${idx}`} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={color} stopOpacity={0.35} />
                                            <stop offset="95%" stopColor={color} stopOpacity={0.02} />
                                        </linearGradient>
                                    );
                                })
                            )}
                        </defs>

                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}
                        />

                        <XAxis
                            dataKey="monthShort"
                            stroke={isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'}
                            tick={{ fontSize: 12 }}
                        />

                        {/* Y-Axes Rendering */}
                        {seriesType === 'metric' ? (
                            seriesKeys.map((key) => {
                                const config = MEASURE_CONFIG[key] || {};
                                const domain = axisConfigs[key];
                                return (
                                    <YAxis
                                        key={key}
                                        yAxisId={config.axisId || 'left'}
                                        orientation={config.axisOrientation || 'left'}
                                        stroke={config.color}
                                        domain={domain ? [domain.min, domain.max] : ['auto', 'auto']}
                                        tick={{ fontSize: 11 }}
                                        tickFormatter={(v) => `${v}${config.unit}`}
                                        hide={seriesKeys.length > 2 && key !== 'TEMP' && key !== 'POWE'}
                                    />
                                );
                            })
                        ) : (
                            <YAxis
                                yAxisId="singleAxis"
                                orientation="left"
                                stroke={MEASURE_CONFIG[measureType]?.color || (isDark ? '#fff' : '#000')}
                                domain={axisConfigs.single ? [axisConfigs.single.min, axisConfigs.single.max] : ['auto', 'auto']}
                                tick={{ fontSize: 11 }}
                                tickFormatter={(v) => `${v} ${getMeasureUnit(measureType)}`}
                            />
                        )}

                        <Tooltip
                            content={
                                <CustomTooltip
                                    seriesType={seriesType}
                                    measureType={measureType}
                                    t={t}
                                    selectedYear={selectedYear}
                                />
                            }
                        />

                        <Legend
                            wrapperStyle={{ paddingTop: 10 }}
                            formatter={(value) => {
                                if (seriesType === 'metric') {
                                    return (
                                        <span style={{ color: isDark ? '#fff' : '#333', fontSize: 13, fontWeight: 500 }}>
                                            {getMeasureLabel(value, t)} ({getMeasureUnit(value)})
                                        </span>
                                    );
                                }
                                return (
                                    <span style={{ color: isDark ? '#fff' : '#333', fontSize: 13, fontWeight: 500 }}>
                                        {value}
                                    </span>
                                );
                            }}
                        />

                        {/* Series rendering */}
                        {chartType === 'area' ? (
                            seriesType === 'metric' ? (
                                seriesKeys.map((key) => {
                                    const config = MEASURE_CONFIG[key] || {};
                                    return (
                                        <Area
                                            key={key}
                                            yAxisId={config.axisId || 'left'}
                                            type="monotone"
                                            dataKey={key}
                                            name={getMeasureLabel(key, t)}
                                            stroke={config.color}
                                            strokeWidth={2}
                                            fill={`url(#grad-${key})`}
                                            activeDot={{ r: 6 }}
                                            connectNulls
                                        />
                                    );
                                })
                            ) : (
                                seriesKeys.map((zoneKey, idx) => {
                                    const color = getZoneColor(idx);
                                    return (
                                        <Area
                                            key={zoneKey}
                                            yAxisId="singleAxis"
                                            type="monotone"
                                            dataKey={zoneKey}
                                            name={zoneKey}
                                            stroke={color}
                                            strokeWidth={2}
                                            fill={`url(#grad-${idx})`}
                                            activeDot={{ r: 6 }}
                                            connectNulls
                                        />
                                    );
                                })
                            )
                        ) : (
                            seriesType === 'metric' ? (
                                seriesKeys.map((key) => {
                                    const config = MEASURE_CONFIG[key] || {};
                                    return (
                                        <Line
                                            key={key}
                                            yAxisId={config.axisId || 'left'}
                                            type="monotone"
                                            dataKey={key}
                                            name={getMeasureLabel(key, t)}
                                            stroke={config.color}
                                            strokeWidth={2.5}
                                            dot={{ r: 4, fill: config.color }}
                                            activeDot={{ r: 7 }}
                                            connectNulls
                                        />
                                    );
                                })
                            ) : (
                                seriesKeys.map((zoneKey, idx) => {
                                    const color = getZoneColor(idx);
                                    return (
                                        <Line
                                            key={zoneKey}
                                            yAxisId="singleAxis"
                                            type="monotone"
                                            dataKey={zoneKey}
                                            name={zoneKey}
                                            stroke={color}
                                            strokeWidth={2.5}
                                            dot={{ r: 4, fill: color }}
                                            activeDot={{ r: 7 }}
                                            connectNulls
                                        />
                                    );
                                })
                            )
                        )}
                    </ChartComponent>
                </ResponsiveContainer>
            </Box>
        </Paper>
    );
}

export default MonthlyTrendChart;
